import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { rmsApi } from '../../../state/axios';
import { convertDateRangeToEpochTimestamp } from '../../../helpers/utils';
import type { RootState } from '../../../state/store';
import type { CustomExportColumn } from '../components/customExportDialog';

interface CustomExportPayload {
  columns: Array<{
    id: string;
    label: string;
    column_type: 'dimension' | 'value';
    aggregation: string | null;
    sql_expression: string;
    sql_alias: string;
  }>;
  time_range: string;
  report_name?: string;
  company_id?: string;
  location_ids?: string;
  currency_code?: string;
}

export const useCustomExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const routeMatch = useParams();
  const companyDetails = useSelector((state: RootState) => state.company) as any;
  const { filters } = useSelector((state: RootState) => state);
  const { dates, timeRange, locations: locationFilters } = filters;

  const initiateCustomExport = useCallback(
    async (selectedColumns: CustomExportColumn[], reportName: string) => {
      if (selectedColumns.length === 0) {
        throw new Error('No columns selected for export');
      }

      if (!reportName || !reportName.trim()) {
        throw new Error('Report name is required');
      }

      setIsExporting(true);

      try {
        // Build the export URL for custom report
        const dashboard = routeMatch?.dashboard;
        const widget = routeMatch?.widget;
        const baseUrl = `/api/v1/dar/${dashboard}/${widget}/report/download/custom_report`;

        // Prepare time range
        const finalTimeRange = convertDateRangeToEpochTimestamp(
          dates as [string, string],
          timeRange as [[number, number], [number, number]],
          companyDetails?.company_details?.time_zone_offset || '+00:00'
        );

        // Prepare request payload - columns already have snake_case from backend
        const payload: CustomExportPayload = {
          columns: selectedColumns.map((col) => ({
            id: col.id,
            label: col.label,
            column_type: col.column_type,
            aggregation: col.aggregation,
            sql_expression: col.sql_expression,
            sql_alias: col.sql_alias
          })),
          time_range: `${finalTimeRange[0]}:${finalTimeRange[1]}`,
          report_name: reportName.trim()
        };

        // Add company_id
        if (companyDetails?.company_details?.company_id) {
          payload.company_id = companyDetails.company_details.company_id.toString();
        }

        // Add location_ids if present
        if (locationFilters && locationFilters.length > 0) {
          payload.location_ids = locationFilters.join(',');
        }

        // Add currency code
        if (companyDetails?.company_details?.currency_code) {
          payload.currency_code = companyDetails.company_details.currency_code;
        }

        // Make API call
        const response = await rmsApi.post(baseUrl, payload);

        if (response.status === 200) {
          Swal.fire({
            title: 'Export Initiated',
            text: 'Your custom export has been started. You will receive an email when ready.',
            icon: 'success',
            timer: 3000,
            showConfirmButton: false
          });
          return { success: true };
        } else {
          throw new Error(`Export failed with status: ${response.status}`);
        }
      } catch (error: any) {
        console.error('Custom export error:', error);

        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          'Failed to initiate custom export';

        Swal.fire({
          title: 'Export Failed',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK'
        });

        throw error;
      } finally {
        setIsExporting(false);
      }
    },
    [routeMatch, dates, timeRange, companyDetails, locationFilters]
  );

  return {
    initiateCustomExport,
    isExporting
  };
};
